import { ReportCompetenceType, ReportCoverSheet, ReportSummarySheet, ReportType } from '@/api/generated/api.enum';
import type { Components } from '@/api/generated/api';
import { IReportConfiguration } from '@/interfaces';
import { isEnumValue } from 'common/utils/enum.utils';
import { isCoverSheetOfReportType } from 'common/utils/report.utils';
import { defineStore } from 'pinia';

const DEFAULT_CONFIGURATIONS: Readonly<Record<Components.Schemas.ReportType, Readonly<IReportConfiguration>>> = {
  [ReportType.standard]: {
    coverSheet: ReportCoverSheet.withChart,
    competenceType: ReportCompetenceType.withPerformanceAssessments,
    assessedCompetencesOnly: true,
    showGradeRecommendation: false,
    showCompetenceLevelDescriptions: true,
    showNoteCompetences: true,
    summarySheet: ReportSummarySheet.simple,
  },
  // notes reports have no competence layout, so only their cover, summary sheet and competence column vary
  [ReportType.notesReport]: {
    coverSheet: ReportCoverSheet.withNote,
    competenceType: null,
    assessedCompetencesOnly: false,
    showGradeRecommendation: false,
    showCompetenceLevelDescriptions: false,
    showNoteCompetences: true,
    summarySheet: ReportSummarySheet.simple,
  },
};

// each report type has its own covers; the backend rejects the others
const isCoverSheetOfType = (
  coverSheet: Components.Schemas.ReportCoverSheet | null,
  type: Components.Schemas.ReportType,
): boolean =>
  coverSheet === null || (isEnumValue(ReportCoverSheet, coverSheet) && isCoverSheetOfReportType(coverSheet, type));

export const useReportConfigurationStore = defineStore('reportConfiguration', {
  state: (): { configurations: Record<Components.Schemas.ReportType, IReportConfiguration> } => ({
    configurations: {
      [ReportType.standard]: { ...DEFAULT_CONFIGURATIONS[ReportType.standard] },
      [ReportType.notesReport]: { ...DEFAULT_CONFIGURATIONS[ReportType.notesReport] },
    },
  }),

  getters: {
    getDefaultConfiguration:
      () =>
      (type: Components.Schemas.ReportType): IReportConfiguration => ({ ...DEFAULT_CONFIGURATIONS[type] }),

    // a stored option may no longer exist, so it falls back to the type's default
    getSelectedConfiguration:
      (state) =>
      (type: Components.Schemas.ReportType): IReportConfiguration => {
        const defaults = DEFAULT_CONFIGURATIONS[type];
        const { coverSheet, competenceType, summarySheet, ...options } = state.configurations[type];

        return {
          ...options,
          coverSheet: isCoverSheetOfType(coverSheet, type) ? coverSheet : defaults.coverSheet,
          competenceType:
            competenceType === null || isEnumValue(ReportCompetenceType, competenceType)
              ? competenceType
              : defaults.competenceType,
          summarySheet:
            summarySheet === null || isEnumValue(ReportSummarySheet, summarySheet)
              ? summarySheet
              : defaults.summarySheet,
        };
      },
  },

  actions: {
    setSelectedConfiguration(type: Components.Schemas.ReportType, selected: IReportConfiguration) {
      this.configurations[type] = { ...selected };
    },
  },

  persist: {
    key: 'report-configuration-store',
    // the flat standard configuration of older releases is not picked, so it starts over from the defaults
    pick: ['configurations'],
  },
});
